import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { PROPERTIES } from "@/data/site";
import { getPropertyContent, savePropertyContent } from "@/lib/api";

const field =
  "w-full border border-[#E5E5E5] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0F2A52]";
const label = "block text-xs uppercase tracking-wider text-neutral-500 mb-2";

const toLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
const fromLines = (text) => text.split("\n").map((s) => s.trim()).filter(Boolean);

export const PropertyEditor = () => {
  const [slug, setSlug] = useState(PROPERTIES[0].slug);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    getPropertyContent().then(({ data }) => setOverrides(data)).catch(() => setOverrides({}));
  }, []);

  useEffect(() => {
    const base = PROPERTIES.find((p) => p.slug === slug);
    const merged = { ...base, ...(overrides[slug] || {}) };
    setForm({
      name: merged.name || "",
      tagline: merged.tagline || "",
      location: merged.location || "",
      short: merged.short || "",
      rate: merged.rate ?? "",
      bedrooms: merged.bedrooms ?? "",
      bathrooms: merged.bathrooms ?? "",
      sleeps: merged.sleeps ?? "",
      description: toLines(merged.description),
      locationNote: merged.locationNote || "",
      features: toLines(merged.features),
      amenities: toLines(merged.amenities),
    });
  }, [slug, overrides]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSave = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        tagline: form.tagline,
        location: form.location,
        short: form.short,
        rate: form.rate === "" ? null : Number(form.rate),
        bedrooms: form.bedrooms === "" ? null : Number(form.bedrooms),
        bathrooms: form.bathrooms === "" ? null : Number(form.bathrooms),
        sleeps: form.sleeps === "" ? null : Number(form.sleeps),
        description: fromLines(form.description),
        locationNote: form.locationNote,
        features: fromLines(form.features),
        amenities: fromLines(form.amenities),
      };
      await savePropertyContent(slug, payload);
      setOverrides((o) => ({ ...o, [slug]: payload }));
      toast.success("Saved — the website is updated");
    } catch {
      toast.error("Could not save those details");
    } finally {
      setBusy(false);
    }
  };

  if (!form) return null;

  return (
    <div className="mt-8 grid gap-4" data-testid="owner-details-tab">
      <div className="bg-white border border-[#E5E5E5] rounded-md p-5">
        <label className={label}>Property</label>
        <select
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className={`${field} md:w-96`}
          data-testid="owner-details-property-select"
        >
          {PROPERTIES.map((p) => (
            <option key={p.slug} value={p.slug}>
              {(overrides[p.slug] && overrides[p.slug].name) || p.name}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-md p-5 grid gap-5">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className={label}>Property name</label>
            <input className={field} value={form.name} onChange={set("name")} data-testid="edit-name" />
          </div>
          <div>
            <label className={label}>Headline under the name</label>
            <input className={field} value={form.tagline} onChange={set("tagline")} data-testid="edit-tagline" />
          </div>
          <div>
            <label className={label}>Location (suburb, region)</label>
            <input className={field} value={form.location} onChange={set("location")} data-testid="edit-location" />
          </div>
          <div>
            <label className={label}>Nightly rate in AUD (blank = "Rates on enquiry")</label>
            <input
              type="number"
              className={field}
              value={form.rate}
              onChange={set("rate")}
              data-testid="edit-rate"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-5">
          <div>
            <label className={label}>Bedrooms</label>
            <input type="number" className={field} value={form.bedrooms} onChange={set("bedrooms")} data-testid="edit-bedrooms" />
          </div>
          <div>
            <label className={label}>Bathrooms</label>
            <input type="number" className={field} value={form.bathrooms} onChange={set("bathrooms")} data-testid="edit-bathrooms" />
          </div>
          <div>
            <label className={label}>Sleeps</label>
            <input type="number" className={field} value={form.sleeps} onChange={set("sleeps")} data-testid="edit-sleeps" />
          </div>
        </div>

        <div>
          <label className={label}>Short summary (shown on the home page card)</label>
          <textarea rows={2} className={field} value={form.short} onChange={set("short")} data-testid="edit-short" />
        </div>

        <div>
          <label className={label}>Full description — one paragraph per line</label>
          <textarea rows={8} className={field} value={form.description} onChange={set("description")} data-testid="edit-description" />
        </div>

        <div>
          <label className={label}>Location notes (what's nearby, distances)</label>
          <textarea rows={3} className={field} value={form.locationNote} onChange={set("locationNote")} data-testid="edit-location-note" />
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className={label}>Key features — one per line</label>
            <textarea rows={7} className={field} value={form.features} onChange={set("features")} data-testid="edit-features" />
          </div>
          <div>
            <label className={label}>Amenities — one per line</label>
            <textarea rows={7} className={field} value={form.amenities} onChange={set("amenities")} data-testid="edit-amenities" />
          </div>
        </div>

        <button
          onClick={onSave}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-[#0F2A52] text-white rounded-md px-4 py-2.5 text-sm w-fit disabled:opacity-60"
          data-testid="owner-save-details"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          {busy ? "Saving" : "Save changes"}
        </button>
      </div>
    </div>
  );
};

export default PropertyEditor;
